import { useEffect, useRef, useCallback } from 'react';
import { useAppSelector, useAppDispatch } from './useAppSelector';
import { checkSessionExpiration } from '../../store/slices/authSlice';

export const useSessionTimeout = () => {
  const dispatch = useAppDispatch();
  const { isAuthenticated, userCode, sessionStartTime, sessionTimeout } = useAppSelector(state => state.auth);
  const intervalRef = useRef(null);

  // Verificar expiración de sesión con intervalos dinámicos
  useEffect(() => {
    if (isAuthenticated && userCode && userCode !== 'ACCESO_LIBRE') {
      const checkExpiration = () => {
        const currentTime = Date.now();
        const timeElapsed = currentTime - sessionStartTime;
        const remainingTime = sessionTimeout - timeElapsed;
        
        // Si ya expiró, hacer logout inmediatamente
        if (remainingTime <= 0) {
          dispatch(checkSessionExpiration());
          return;
        }
        
        // Determinar el intervalo de verificación basado en el tiempo restante
        let checkInterval;
        if (remainingTime <= 30000) { // Últimos 30 segundos: verificar cada segundo
          checkInterval = 1000;
        } else if (remainingTime <= 60000) { // Último minuto: verificar cada 5 segundos
          checkInterval = 5000;
        } else if (remainingTime <= 300000) { // Últimos 5 minutos: verificar cada 30 segundos
          checkInterval = 30000;
        } else { // Más de 5 minutos: verificar cada minuto
          checkInterval = 60000;
        }
        
        // Programar próxima verificación
        intervalRef.current = setTimeout(() => {
          checkExpiration();
        }, checkInterval);
      };
      
      // Iniciar verificación inmediatamente
      checkExpiration();
    } else {
      // Limpiar intervalos si no está autenticado o es acceso libre
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
        intervalRef.current = null;
      }
    }

    return () => {
      if (intervalRef.current) {
        clearTimeout(intervalRef.current);
      }
    };
  }, [isAuthenticated, userCode, sessionStartTime, sessionTimeout, dispatch]);

  // Función para obtener tiempo restante de sesión
  const getRemainingTime = useCallback(() => {
    if (!isAuthenticated || !userCode || userCode === 'ACCESO_LIBRE' || !sessionStartTime) {
      return null;
    }

    const currentTime = Date.now();
    const timeElapsed = currentTime - sessionStartTime;
    const remainingTime = sessionTimeout - timeElapsed;

    if (remainingTime <= 0) {
      return 0;
    }

    return remainingTime;
  }, [isAuthenticated, userCode, sessionStartTime, sessionTimeout]);

  // Función para formatear tiempo restante
  const getFormattedRemainingTime = useCallback(() => {
    const remainingTime = getRemainingTime();
    
    if (remainingTime === null) {
      return null;
    }

    if (remainingTime <= 0) {
      return 'Sesión expirada';
    }

    const minutes = Math.floor(remainingTime / 60000);
    const seconds = Math.floor((remainingTime % 60000) / 1000);
    
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }, [getRemainingTime]);

  return {
    getRemainingTime,
    getFormattedRemainingTime,
    isSessionActive: isAuthenticated && userCode && userCode !== 'ACCESO_LIBRE',
  };
}; 